import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  FlaskConical,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";

import { Badge, Button, Card } from "@/design-system/primitives";
import {
  useClearSyntheticCases,
  useClinicianQueue,
  useClinicianRulesAutomations,
  useClinicianSession,
  useSeedSyntheticCases,
  useSyncQueue,
  useSyncStatus,
} from "@/lib/api/clinicianHooks";
import type { ClinicianInboxTagRule, QueueItem } from "@/types/clinician";
import {
  matchesInboxFilter,
  matchesInboxTag,
  scopedInboxItems,
  type InboxFilter,
  type InboxScope,
  type InboxSort,
} from "./inboxFilters";
import { queuePriorityTags } from "./queuePriorityTags";
import { QueueTagBadges } from "./QueueTagBadges";
import { OwnerOptions, useOwnerFilter } from "./ownerFilter";

const FILTERS: { key: InboxFilter; label: string }[] = [
  { key: "nursing", label: "Nursing" },
  { key: "prescriber", label: "Needs prescriber" },
  { key: "needs", label: "Needs reply" },
  { key: "urgent", label: "Safety" },
  { key: "overdue", label: "Overdue" },
  { key: "unassigned", label: "Available" },
  { key: "sent", label: "Sent today" },
  { key: "waiting", label: "Waiting" },
  { key: "snoozed", label: "Snoozed" },
  { key: "older", label: "30+ days" },
  { key: "inactive", label: "Inactive plans" },
  { key: "resolved", label: "Resolved" },
  { key: "automated", label: "AI handled" },
  { key: "all", label: "All" },
];

const WORKFLOW_TAGS: ClinicianInboxTagRule[] = [
  {
    id: "system:reopened_after_reply",
    key: "reopened_after_reply",
    label: "Reopened after reply",
    rule: "The patient replied after a clinician response.",
    priority: 2,
    enabled: true,
    locked: true,
    kind: "topic",
  },
];

export function InboxScreen() {
  const [routeParams, setRouteParams] = useSearchParams();
  const [selectedFilter, setSelectedFilter] = useState<InboxFilter | null>(
    () => (routeParams.get("filter") as InboxFilter | null) ?? null,
  );
  const [search, setSearch] = useState(() => routeParams.get("search") ?? "");
  const [category, setCategory] = useState(
    () => routeParams.get("category") ?? "",
  );
  const [selectedTags, setSelectedTags] = useState<string[]>(() =>
    Array.from(new Set(routeParams.getAll("tag").filter(Boolean))),
  );
  const [ageHours, setAgeHours] = useState(() => routeParams.get("age") ?? "");
  const [localOwner, setLocalOwner] = useState(
    () => routeParams.get("owner") ?? "",
  );
  const [sort, setSort] = useState<InboxSort>(
    () => (routeParams.get("sort") as InboxSort | null) ?? "smart",
  );
  const { data: session } = useClinicianSession();
  const filter: InboxFilter =
    selectedFilter ??
    (session?.provider_type === "nurse" ? "nursing" : "prescriber");
  // Supervisors who prescribe start on their own patients (team routing).
  const ownerFilter = useOwnerFilter();
  const supervisor = ownerFilter.supervisor;
  const { data, isLoading, isError } = useClinicianQueue(
    true,
    ownerFilter.clinicianId,
    ownerFilter.ready,
  );
  const rules = useClinicianRulesAutomations();
  const sync = useSyncQueue();
  const status = useSyncStatus();
  const seed = useSeedSyntheticCases();
  const clearSynthetic = useClearSyntheticCases();
  const navigate = useNavigate();

  const allItems = data?.items ?? [];
  const categories = Array.from(
    new Set(allItems.map((item) => item.category).filter(Boolean)),
  ).sort() as string[];
  const owners = Array.from(
    new Map(
      allItems
        .map((item) => item.patient_owner)
        .filter(Boolean)
        .map((owner) => [owner!.clinician_id, owner!]),
    ).values(),
  );
  const scope: InboxScope = {
    filter,
    query: search,
    category,
    tags: selectedTags,
    ageHours,
    ownerId: localOwner,
    sort,
  };
  const items = scopedInboxItems(allItems, scope);
  const tagDefinitions = [
    ...WORKFLOW_TAGS,
    ...(rules.data?.tags ?? []).filter((definition) => definition.enabled),
  ];
  const itemsBeforeTagFilter = scopedInboxItems(allItems, {
    ...scope,
    tags: [],
  });
  const visibleTagDefinitions = tagDefinitions.filter(
    (definition) =>
      selectedTags.includes(definition.key) ||
      itemsBeforeTagFilter.some((item) =>
        matchesInboxTag(item, definition.key),
      ),
  );
  const unavailableSelectedTags = selectedTags.filter(
    (key) => !tagDefinitions.some((definition) => definition.key === key),
  );
  const updateSelectedTags = (next: string[]) => {
    const unique = Array.from(new Set(next.filter(Boolean)));
    setSelectedTags(unique);
    const updated = new URLSearchParams(routeParams);
    updated.delete("tag");
    unique.forEach((tag) => updated.append("tag", tag));
    setRouteParams(updated, { replace: true });
  };
  const syntheticCount = allItems.filter((item) => item.synthetic).length;
  const isNonProduction =
    !import.meta.env.PROD || import.meta.env.VITE_APP_ENV === "staging";

  const currentStatus = status.data;
  const syncing = sync.isPending || Boolean(currentStatus?.running);
  const syncLabel = syncing
    ? `Syncing · ${currentStatus?.counts.threads ?? 0} threads`
    : currentStatus?.error
      ? "Sync failed · retry"
      : currentStatus?.finished_at
        ? `Recovery complete · ${currentStatus.counts.created} added`
        : "Recovery sync";

  return (
    <div className="max-w-[120rem] mx-auto px-20 md:px-40 py-30 md:py-40 flex flex-col gap-25">
      <header className="flex flex-wrap items-end justify-between gap-15">
        <div className="flex flex-col gap-6">
          <span className="type-label-sm text-ink-muted">
            CLINICAL WORK QUEUE
          </span>
          <h1 className="type-heading-xl">Patient messages</h1>
          {data && (
            <div className="flex flex-wrap items-center gap-10 type-body-sm text-ink-muted">
              <span>{data.open_count} awaiting clinical action</span>
              {data.urgent_count > 0 && (
                <span className="inline-flex items-center gap-5 text-danger font-medium">
                  <AlertTriangle size={13} aria-hidden /> {data.urgent_count}{" "}
                  safety
                </span>
              )}
              <span>
                {
                  allItems.filter(
                    (item) => item.sla_breached && item.needs_clinician,
                  ).length
                }{" "}
                overdue
              </span>
              <span className="inline-flex items-center gap-5">
                <span className="w-6 h-6 rounded-full bg-success" aria-hidden />
                Live via Healthie webhook
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-8">
          {supervisor && (
            <select
              aria-label="Filter by clinician owner"
              className="rounded border border-line bg-raised px-9 py-7 type-label-sm"
              value={ownerFilter.selected}
              onChange={(event) => ownerFilter.select(event.target.value)}
            >
              <OwnerOptions owner={ownerFilter} />
            </select>
          )}
          {isNonProduction && (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={seed.isPending}
                onClick={() => seed.mutate(32)}
              >
                <FlaskConical size={13} aria-hidden />
                Load demo cases
              </Button>
              {syntheticCount > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={clearSynthetic.isPending}
                  onClick={() => clearSynthetic.mutate()}
                >
                  <Trash2 size={13} aria-hidden />
                  Clear {syntheticCount} demo
                </Button>
              )}
            </>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={syncing}
            onClick={() => sync.mutate()}
          >
            <RefreshCw size={13} aria-hidden />
            {syncLabel}
          </Button>
        </div>
      </header>

      {isNonProduction && syntheticCount > 0 && (
        <div className="flex items-start gap-8 border border-info/30 bg-info-wash px-15 py-10 type-body-sm text-info">
          <FlaskConical size={15} className="mt-1 shrink-0" aria-hidden />
          <span>
            Demo cases are entirely fictional and stay inside staging. Their
            messages never reach Healthie.
          </span>
        </div>
      )}

      <div
        className="flex flex-wrap gap-6"
        role="group"
        aria-label="Queue filters"
      >
        {FILTERS.map((entry) => {
          const count = allItems.filter((item) =>
            matchesInboxFilter(item, entry.key),
          ).length;
          const active = filter === entry.key;
          return (
            <button
              key={entry.key}
              type="button"
              onClick={() => setSelectedFilter(entry.key)}
              aria-pressed={active}
              className={`min-h-32 px-12 rounded-pill type-label-sm border focus-visible:outline-none focus-visible:shadow-focus ${
                active
                  ? "bg-ink text-page border-ink"
                  : entry.key === "urgent" && count > 0
                    ? "border-danger/30 bg-danger-wash text-danger"
                    : entry.key === "overdue" && count > 0
                      ? "border-warning/30 bg-warning-wash text-warning"
                      : "border-line text-ink-muted hover:bg-sunken"
              }`}
            >
              {entry.label}
              {count > 0 && (
                <span className="ml-5 tabular-nums opacity-70">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-8">
          <label className="relative block min-w-[22rem] max-w-[54rem] flex-1">
            <Search
              size={15}
              className="absolute left-11 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search patient, message, category, or status"
              aria-label="Search patient conversations"
              className="w-full min-h-40 pl-36 pr-12 border border-line bg-raised type-body-sm outline-none focus:shadow-focus"
            />
          </label>
          <select
            aria-label="Filter by message category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            className="min-h-40 border border-line bg-raised px-10 type-body-sm"
          >
            <option value="">All categories</option>
            {categories.map((value) => (
              <option key={value} value={value}>
                {labelize(value)}
              </option>
            ))}
          </select>
          <select
            aria-label="Sort conversations"
            value={sort}
            onChange={(event) => setSort(event.target.value as InboxSort)}
            className="min-h-40 border border-line bg-raised px-10 type-body-sm"
          >
            <option value="smart">Smart priority</option>
            <option value="oldest">Oldest patient first</option>
            <option value="newest">Newest patient first</option>
          </select>
          <select
            aria-label="Filter by message age"
            value={ageHours}
            onChange={(event) => setAgeHours(event.target.value)}
            className="min-h-40 border border-line bg-raised px-10 type-body-sm"
          >
            <option value="">Any date</option>
            <option value="24">Last 24 hours</option>
            <option value="72">Last 3 days</option>
            <option value="168">Last 7 days</option>
            <option value="720">Last 30 days</option>
          </select>
          {!supervisor && owners.length > 1 && (
            <select
              aria-label="Filter by primary prescriber"
              value={localOwner}
              onChange={(event) => setLocalOwner(event.target.value)}
              className="min-h-40 border border-line bg-raised px-10 type-body-sm"
            >
              <option value="">All prescribers</option>
              {owners.map((owner) => (
                <option key={owner.clinician_id} value={owner.clinician_id}>
                  {owner.display_name || owner.email}
                </option>
              ))}
            </select>
          )}
        </div>
        {(visibleTagDefinitions.length > 0 || selectedTags.length > 0) && (
          <div
            className="flex flex-wrap items-center gap-5"
            role="group"
            aria-label="Filter by inbox tags"
          >
            <span className="mr-2 type-label-xs text-ink-muted">
              TAGS / SIGNALS
            </span>
            {visibleTagDefinitions.map((definition) => {
              const active = selectedTags.includes(definition.key);
              const count = itemsBeforeTagFilter.filter((item) =>
                matchesInboxTag(item, definition.key),
              ).length;
              return (
                <button
                  key={definition.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() =>
                    updateSelectedTags(
                      selectedTags.includes(definition.key)
                        ? selectedTags.filter((key) => key !== definition.key)
                        : [...selectedTags, definition.key],
                    )
                  }
                  className={`min-h-25 rounded-pill border px-9 type-label-xs focus-visible:outline-none focus-visible:shadow-focus ${
                    active
                      ? definition.kind === "safety"
                        ? "border-danger bg-danger text-white"
                        : "border-ink bg-ink text-white"
                      : definition.kind === "safety"
                        ? "border-danger/30 bg-danger-wash text-danger"
                        : "border-line bg-raised text-ink-secondary hover:border-ink"
                  }`}
                >
                  {definition.label}
                  <span className="ml-4 tabular-nums opacity-70">{count}</span>
                </button>
              );
            })}
            {unavailableSelectedTags.map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed="true"
                title="This tag is no longer available; click to remove the filter."
                onClick={() =>
                  updateSelectedTags(selectedTags.filter((tag) => tag !== key))
                }
                className="min-h-25 rounded-pill border border-ink bg-ink px-9 type-label-xs text-white focus-visible:outline-none focus-visible:shadow-focus"
              >
                {labelize(key)}
                <span className="ml-4 tabular-nums opacity-70">0</span>
              </button>
            ))}
            {selectedTags.length > 0 && (
              <button
                type="button"
                aria-label="Clear inbox tag filters"
                onClick={() => updateSelectedTags([])}
                className="min-h-25 px-7 type-label-xs text-ink-muted underline underline-offset-2 hover:text-ink"
              >
                Clear
              </button>
            )}
          </div>
        )}
        <p className="type-body-sm text-ink-muted">
          Current reply queues show the last 30 days for patients without a
          definitively ended plan. Older and inactive-plan conversations stay
          available in their own filters; Safety always remains visible. AI
          handled contains messages the assistant resolved without adding them
          to the active work queue.
        </p>
      </div>

      {(isLoading || !ownerFilter.ready) && <QueueSkeleton />}
      {isError && (
        <Card
          className="p-40 text-center type-body-md text-ink-muted"
          role="alert"
        >
          The clinical queue could not be fetched. It will retry automatically.
        </Card>
      )}

      {data && items.length === 0 && (
        <Card className="p-40 text-center flex flex-col items-center gap-10">
          <CheckCircle2 size={20} aria-hidden />
          <p className="type-heading-md">
            {search.trim()
              ? "No matching conversations"
              : "No work in this view"}
          </p>
          <p className="type-body-sm text-ink-muted max-w-[44rem]">
            {search.trim()
              ? `Nothing in ${FILTERS.find((entry) => entry.key === filter)?.label ?? "this view"} matches “${search.trim()}”.`
              : "New patient replies reopen automatically. Use another filter to review waiting or resolved work."}
          </p>
        </Card>
      )}

      {data && items.length > 0 && (
        <div className="border border-line-subtle bg-raised divide-y divide-line-subtle">
          {items.map((item) => (
            <QueueRow
              key={item.conversation_id}
              item={item}
              tagDefinitions={rules.data?.tags ?? []}
              onOpen={() => {
                const params = new URLSearchParams();
                params.set("filter", filter);
                if (search) params.set("search", search);
                if (category) params.set("category", category);
                selectedTags.forEach((tag) => params.append("tag", tag));
                if (ageHours) params.set("age", ageHours);
                if (localOwner) params.set("owner", localOwner);
                if (sort !== "smart") params.set("sort", sort);
                if (supervisor) params.set("clinician", ownerFilter.selected);
                navigate({
                  pathname: `/clinician-portal/inbox/${item.conversation_id}`,
                  search: params.toString(),
                });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function QueueSkeleton() {
  return (
    <div
      className="border border-line-subtle bg-raised divide-y divide-line-subtle"
      aria-label="Fetching queue"
    >
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className="p-15 flex flex-col gap-8 animate-pulse">
          <div className="h-14 w-1/3 bg-sunken" />
          <div className="h-12 w-3/4 bg-sunken" />
        </div>
      ))}
    </div>
  );
}

function formatWait(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 48) return `${Math.floor(hours)}h`;
  return `${Math.floor(hours / 24)}d`;
}

function labelize(value: string | null): string {
  if (!value) return "Clinical review";
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function QueueRow({
  item,
  tagDefinitions,
  onOpen,
}: {
  item: QueueItem;
  tagDefinitions: ClinicianInboxTagRule[];
  onOpen: () => void;
}) {
  const name =
    item.patient.display_name || item.patient.email || "Unknown patient";
  const isSafety = item.urgency === 3 && item.needs_clinician;
  const waiting = formatWait(item.waiting_hours);
  const priorityTags = queuePriorityTags(item, tagDefinitions);

  return (
    <div
      className={`relative w-full min-h-80 text-left px-15 py-12 grid md:grid-cols-[minmax(15rem,0.8fr)_minmax(24rem,1.7fr)_minmax(12rem,0.7fr)] gap-10 items-center hover:bg-sunken/60 ${
        item.reopened
          ? "border-l-2 border-success bg-success-wash/30"
          : item.workflow_state === "resolved" ||
              item.workflow_state === "closed"
            ? "bg-success-wash/20"
            : ""
      }`}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open conversation with ${name}`}
        className="absolute inset-0 z-0 focus-visible:outline-none focus-visible:shadow-focus"
      />

      <div className="relative z-10 pointer-events-none min-w-0 flex flex-col gap-5">
        <div className="flex items-center gap-6 min-w-0">
          <span className="type-body-md font-medium truncate">{name}</span>
          {item.synthetic && (
            <Badge tone="info" size="sm">
              <FlaskConical size={10} aria-hidden /> Demo
            </Badge>
          )}
        </div>
        {item.patient_owner && (
          <span className="type-label-xs text-ink-muted truncate">
            {item.patient_owner.display_name || item.patient_owner.email}
          </span>
        )}
      </div>

      <div className="relative z-10 pointer-events-none min-w-0 flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-6">
          {isSafety && (
            <Badge tone="danger" size="sm">
              <AlertTriangle size={10} aria-hidden /> Safety
            </Badge>
          )}
          {item.reopened && (
            <Badge tone="success" size="sm">
              Reopened after reply
            </Badge>
          )}
          {(item.workflow_state === "waiting_patient" ||
            item.workflow_state === "waiting_internal") && (
            <Badge tone="success" size="sm">
              Responded
            </Badge>
          )}
          {(item.workflow_state === "resolved" ||
            item.workflow_state === "closed") && (
            <Badge tone="success" size="sm">
              Resolved
            </Badge>
          )}
          {item.sla_breached && item.needs_clinician && (
            <Badge tone="warning" size="sm">
              <Clock3 size={10} aria-hidden /> Overdue
            </Badge>
          )}
          {item.preparation_status === "failed" && item.needs_clinician && (
            <Badge tone="danger" size="sm">
              AI unavailable
            </Badge>
          )}
          {item.category === "triage_error" && (
            <Badge tone="danger" size="sm">
              AI triage error
            </Badge>
          )}
          {item.required_capability === "prescriber" &&
            item.needs_clinician && (
              <Badge tone="warning" size="sm">
                Needs prescriber
              </Badge>
            )}
          {item.required_capability === "nursing" && item.needs_clinician && (
            <Badge tone="neutral" size="sm">
              Nursing
            </Badge>
          )}
          {item.resolution_reason === "automated_handled" && (
            <Badge tone="info" size="sm">
              <Sparkles size={10} aria-hidden /> AI handled
            </Badge>
          )}
          {item.patient_message_count > 1 && (
            <Badge tone="neutral" size="sm">
              {item.patient_message_count} messages
            </Badge>
          )}
          <QueueTagBadges tags={priorityTags} />
        </div>
        {item.urgency_reason && item.needs_clinician && (
          <p className="type-body-sm text-ink-secondary line-clamp-2">
            {item.urgency_reason}
          </p>
        )}
        <p className="type-label-xs text-ink-muted line-clamp-1">
          {item.last_patient_message_preview || "No message preview available."}
        </p>
      </div>

      <div className="relative z-10 pointer-events-none md:text-right flex md:flex-col items-center md:items-end gap-5">
        {item.needs_clinician ? (
          <span
            className={`inline-flex items-center gap-5 type-label-sm tabular-nums ${
              item.sla_breached ? "text-warning" : "text-ink"
            }`}
          >
            <Clock3 size={12} aria-hidden /> Patient waiting {waiting}
          </span>
        ) : (
          <span className="type-label-sm text-ink">
            {item.resolution_reason === "automated_handled"
              ? "AI handled"
              : labelize(item.workflow_state)}
          </span>
        )}
        <span className="type-label-xs text-ink-muted">
          {item.assigned_to_email
            ? `${item.assigned_to_email} working`
            : labelize(item.workflow_state)}
        </span>
        {item.last_handoff_at && item.work_owner && (
          <span
            className="type-label-xs text-ink-muted line-clamp-1"
            title={item.handoff_note ?? undefined}
          >
            Handed to {item.work_owner.display_name || item.work_owner.email}
          </span>
        )}
      </div>
    </div>
  );
}
